<template>
    <div class="sessions-dropdown mb-4">
      <label for="sessions-select">Select a session to view:</label>
      <div>
        <el-radio-group
          class="mb-2 mt-2"
          :model-value="selectedDeviceType"
          @update:model-value="value => selectedDeviceType = value"
        >
        <el-radio-button label="Knee Sleeve" />
          <el-radio-button label="Pants" />
        <el-radio-button label="Glove" />
          <el-radio-button label="Band" />
          <el-radio-button label="Neat" />
          <el-radio-button label="Test" />
        </el-radio-group>
        <div class="select-button-container mt-2">
          <el-select
            :model-value="selectedSessions"
            @update:model-value="value => selectedSessions = value"
            multiple
            filterable
            :multiple-limit="3"
            :remote-method="remoteMethod"
            value-key="id"
            class="select-multiple"
            placeholder=""
            :loading="loadingSessions"
          >
            <el-option
              v-for="session in filteredSessions"
              :key="session.id"
              :label="`${session.index} - ${session.id} - ${session.date.toLocaleDateString('en-US', dateOptions)}, ${session.movements[0] ? session.movements[0].name : ''}`"
              :value="session"
            />
            <template #tag>
              <el-tag
                closable
                :disable-transitions="true"
                @click.stop="closeTag(session.id)"
                v-for="session in selectedSessions"
                :key="session.id"
              >
                {{ session.index }} - {{ session.id }} -
                {{ session.date.toLocaleDateString('en-US', dateOptions) }},
                {{ session.movements[0] ? session.movements[0].name : '' }}
              </el-tag>
            </template>
            <template #loading>
              <el-icon class="is-loading">
                <svg class="circular" viewBox="0 0 20 20">
                  <g
                    class="path2 loading-path"
                    stroke-width="0"
                    style="animation: none; stroke: none"
                  >
                    <circle r="3.375" class="dot1" rx="0" ry="0" />
                    <circle r="3.375" class="dot2" rx="0" ry="0" />
                    <circle r="3.375" class="dot4" rx="0" ry="0" />
                    <circle r="3.375" class="dot3" rx="0" ry="0" />
                  </g>
                </svg>
              </el-icon>
            </template>
          </el-select>
          <button
            @click="handleMultipleSessionsSelect"
            class="btn btn-primary btn-sm ml-2"
          >
            View
          </button>
        </div>
      </div>
        <Loading
      :active="loadingSessions"
      :is-full-page="true"
      color="var(--accent-color)"
      loader="bars"
      class="loader"
    />
    </div>
  </template>

<script>
import { ref, watch } from 'vue'
import getSessions from '../../composables/getSessions'
import { debounce } from 'lodash' // Assuming you have lodash, if not, implement or add debounce function
import Loading from 'vue-loading-overlay'
import 'vue-loading-overlay/dist/css/index.css'

export default {
  name: 'SessionsMultipleSelection',
  props: ['userId'],
  components: {
    Loading
  },
  data () {
    return {
      dateOptions: {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }
    }
  },
  setup (props, { emit }) {
    const filteredSessions = ref([])
    const selectedSessions = ref([])
    const selectedDeviceType = ref('Knee Sleeve')
    const deviceTypeDic = {
      'Knee Sleeve': 'Knee Brace',
      Glove: 'Glove',
      Band: 'Band',
      Neat: 'Neat',
      Test: 'Test'
    }
    const { sessions: sesssionsSelectionBasedOnDeviceType, loadUserSessions, loadingSessions } = getSessions()

    const remoteMethod = (query) => {
      if (query) {
        filteredSessions.value = sesssionsSelectionBasedOnDeviceType.filter(session => {
          return session.index.toString().includes(query) || // Convert index to string to perform the check
                     session.id.includes(query) ||
                     (session.movements[0] && session.movements[0].name.includes(query))
        })
      } else {
        filteredSessions.value = [...sesssionsSelectionBasedOnDeviceType] // Reset if no query
      }
    }

    const closeTag = (sessionId) => {
      selectedSessions.value = selectedSessions.value.filter(session => session.id !== sessionId)
    }

    const handleMultipleSessionsSelect = () => {
      emit('onMultipleSessionsSelected', selectedSessions.value.map((session) => {
        return session.id
      }), selectedDeviceType.value)
    }

    // Utilizing debounce to reduce rapid data fetching
    const debounceLoadSessions = debounce(async () => {
      await loadUserSessions(props.userId, [deviceTypeDic[selectedDeviceType.value]], [], [])
      filteredSessions.value = sesssionsSelectionBasedOnDeviceType.value
    }, 300)

    watch(selectedDeviceType, (newVal) => {
      selectedSessions.value = []
      filteredSessions.value = []
      debounceLoadSessions()
      emit('deselectComparsionSessions')
    })

    watch(() => props.userId, async () => {
      selectedSessions.value = []
      filteredSessions.value = []
      debounceLoadSessions()
      emit('deselectComparsionSessions')
    }, { deep: true, immediate: true })

    return {
      remoteMethod,
      filteredSessions,
      selectedSessions,
      closeTag,
      handleMultipleSessionsSelect,
      selectedDeviceType,
      sesssionsSelectionBasedOnDeviceType,
      loadingSessions
    }
  }
}
</script>

  <style>
  .sessions-dropdown {
    display: flex;
    flex-direction: column;
    width: 95%;
    margin: 10px 10px 20px 10px;
  }
  .sessions-select {
    padding: 10px;
    border: none;
    background: #ceebee;
    border-radius: 10px;
  }

  .select-multiple .el-select__input {
    margin-top: 5px !important;
  }

  .select-button-container {
    display: flex;
    align-items: center; /* This ensures that the select and the button are aligned at the same height */
    width: 100%; /* Adjust this as necessary */
  }

  .select-multiple {
    flex-grow: 1; /* This makes the select element grow to occupy available space */
  }

  .btn-sm {
    padding: 0.25rem 0.5rem; /* Smaller padding for a smaller button */
    font-size: 0.875rem; /* Smaller font size for the button text */
    line-height: 1.5; /* Adjust line height for better visual alignment */
  }

  .el-select__selection .el-tag .el-tag__content {
    font-size: 13px !important;
  }

  .el-radio-button__original-radio:checked + .el-radio-button__inner {
    color: #fff !important;
    background-color: rgb(143, 93, 143) !important;
    border-color: rgb(143, 93, 143) !important;
  }

  .el-radio-button__inner {
    font-weight: 400 !important;
    background-color: var(--container-color) !important;
    color: var(--text-color) !important;
    border-color: var(--border-color) !important;
  }

  .el-radio-button__inner:hover {
    color: var(--text-color) !important;
  }

  .el-select-dropdown__loading {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100px;
    font-size: 20px;
  }

  .circular {
    display: inline;
    height: 30px;
    width: 30px;
    animation: loading-rotate 2s linear infinite;
  }
  .path {
    animation: loading-dash 1.5s ease-in-out infinite;
    stroke-dasharray: 90, 150;
    stroke-dashoffset: 0;
    stroke-width: 2;
    stroke: var(--accent-color);
    stroke-linecap: round;
  }
  .loading-path .dot1 {
    transform: translate(3.75px, 3.75px);
    fill: var(--accent-color);
    animation: custom-spin-move 1s infinite linear alternate;
    opacity: 0.3;
  }
  .loading-path .dot2 {
    transform: translate(calc(100% - 3.75px), 3.75px);
    fill: var(--accent-color);
    animation: custom-spin-move 1s infinite linear alternate;
    opacity: 0.3;
    animation-delay: 0.4s;
  }
  .loading-path .dot3 {
    transform: translate(3.75px, calc(100% - 3.75px));
    fill: var(--accent-color);
    animation: custom-spin-move 1s infinite linear alternate;
    opacity: 0.3;
    animation-delay: 1.2s;
  }
  .loading-path .dot4 {
    transform: translate(calc(100% - 3.75px), calc(100% - 3.75px));
    fill: var(--accent-color);
    animation: custom-spin-move 1s infinite linear alternate;
    opacity: 0.3;
    animation-delay: 0.8s;
  }
  @keyframes loading-rotate {
    to {
      transform: rotate(360deg);
    }
  }
  @keyframes loading-dash {
    0% {
      stroke-dasharray: 1, 200;
      stroke-dashoffset: 0;
    }
    50% {
      stroke-dasharray: 90, 150;
      stroke-dashoffset: -40px;
    }
    100% {
      stroke-dasharray: 90, 150;
      stroke-dashoffset: -120px;
    }
  }
  @keyframes custom-spin-move {
    to {
      opacity: 1;
    }
  }
  </style>
